import { useAppShell } from "@salestrak/ui";
import { useCallback, useRef } from "react";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkflowsLanding } from "#app/lib/hooks/use-workflows-landing";
import { useModuleRail } from "./module-rail-context";
import { resolveModuleRoute } from "./tiles";
import type { ResolvedRailTile } from "#app/lib/hooks/use-nav-preferences";
import { AnalyticsContext, NavigationEvent, useAnalytics } from "#app/lib/analytics";

/**
 * The one module-tile press behavior, shared by every tier-1 surface — the
 * vertical rail, the compact drawer's horizontal strip, and the More popup's
 * overflow rows — so a module press means the same thing everywhere.
 *
 * Pressing a tile just navigates; the active tile (and with it the module
 * menu) derives from the route. The Home tile lands on the Home module's own
 * first screen, My Work (see `resolveModuleRoute` — the workspace root is a
 * redirect to the default-view LAUNCH preference, which may name another
 * module, so routing the tile through it makes Home open the Map for anyone
 * whose default view is the Map). The Workflows tile deep-links straight to
 * a workflow's board — the last one the viewer opened, else their first
 * (`useWorkflowsLanding`); with no visible workflows it falls back to
 * `/workflows` (the roster page).
 *
 * On COMPACT a module press does not navigate at all — it just switches which
 * menu the drawer shows (`browseTileId`): the user is choosing where to go,
 * and navigating early yanks the page behind the drawer (Home's root redirect
 * lands on the default view and closed the drawer out from under the pick).
 * The row press is the commit — or a DOUBLE tap on the same tile, which
 * commits to the module's default view (the same route the tile navigates to
 * on the other tiers: Home's My Work, the first workflow's board, a roster
 * module's first row) and dismisses the drawer. On the other tiers a press
 * only navigates — it never presents a dismissed pane. Whoever put the pane away asked for a
 * rail-only shell, and switching modules is not a request to undo that; the
 * pane comes back only through the rail's expand control or by entering
 * settings (`SettingsNavAutoOpen`, where the pane IS the navigation). Tiles
 * without a module menu give the user nothing to pick from, so those navigate
 * and dismiss the drawer instead.
 */
/** Two taps on one tile inside this window read as one double tap. */
const DOUBLE_TAP_MS = 350;

export function useModuleTilePress(): (tile: ResolvedRailTile) => void {
  const { tier } = useAppShell();
  const closeSidebar = useCloseSidebarIfMobile();
  const { setBrowseTileId } = useModuleRail();
  const { push: navigate } = useAppNavigate();
  const workflowId = useWorkflowsLanding();
  // The last compact browse press, for the double-tap commit.
  const lastPress = useRef<{ tileId: string; at: number } | null>(null);
  const { track } = useAnalytics();

  return useCallback(
    (tile: ResolvedRailTile) => {
      const route = resolveModuleRoute(tile, { workflowId });
      // The shell tier names the surface: the rail, or the compact strip,
      // where a press picks a menu before it navigates.
      track({
        context: AnalyticsContext.Navigation,
        event: NavigationEvent.ModuleOpen,
        data: { module: tile.id, via: "tile", tier },
      });
      if (tile.hasSubmenu ?? true) {
        if (tier === "compact") {
          const now = Date.now();
          const previous = lastPress.current;
          lastPress.current = { tileId: tile.id, at: now };
          const doubleTap =
            previous?.tileId === tile.id && now - previous.at <= DOUBLE_TAP_MS;
          if (!doubleTap) {
            setBrowseTileId(tile.id);
            return;
          }
          lastPress.current = null;
          closeSidebar();
        }
      } else {
        closeSidebar();
      }
      navigate(route);
    },
    [
      workflowId,
      setBrowseTileId,
      tier,
      closeSidebar,
      navigate,
      track,
    ],
  );
}
